import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Plus, Filter, ListTodo } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import { CreateTaskModal } from "./components/CreateTaskModal";
import { EditTaskModal } from "./components/EditTaskModal";
import { SubmitTaskModal } from "./components/SubmitTaskModal";
import { ViewSubmissionModal } from "./components/ViewSubmissionModal";
import { TaskCard } from "./components/TaskCard";
import { TaskSubmissionCard } from "./components/TaskSubmissionCard";
import { UserTaskCard } from "./components/UserTaskCard";
import { SuggestionCard, TaskSuggestion } from "./components/SuggestionCard";
import { useModal } from "../../hooks/useModal";
import type { Task, TaskSubmission, UserTask, CreateTaskInput, SubmitTaskInput, TaskDepartment } from "../../types/task";
import { ApiService } from "../../services/api";
import TaskStatusFilter, { TaskStatusFilterType } from "./components/TaskStatusFilter";
import checkPermission from "../../utils/permission";

export default function TasksPage() {
  const { showAlert, showConfirm, showPrompt } = useModal();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [viewSubmissionModalOpen, setViewSubmissionModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<UserTask | null>(null);
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<Task | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<TaskSubmission | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  const [filterOpen, setFilterOpen] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaskStatusFilterType>("ALL");
  const [showSubmissionsForTask, setShowSubmissionsForTask] = useState<string | null>(null);
  const [prefillTaskData, setPrefillTaskData] = useState<CreateTaskInput | null>(null);

  const canListTasks = checkPermission("task_list");
  const canCreateTask = checkPermission("task_create");
  const canUpdateTask = checkPermission("task_update");
  const canDeleteTask = checkPermission("task_delete");
  const canSubmitTask = checkPermission("task_submit");
  const canReviewTask = checkPermission("task_review");

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const mql = window.matchMedia ? window.matchMedia("(max-width: 640px)") : null;
        const compute = () => {
          const isMobile = !!mql?.matches;
          setIsMobileViewport(isMobile);
          if (isMobile) setFilterOpen(false);
        };
        compute();
        if (mql) {
          if ("addEventListener" in mql) mql.addEventListener("change", compute);
          // @ts-expect-error - Safari
          else mql.addListener(compute);
          return () => {
            if ("removeEventListener" in mql) mql.removeEventListener("change", compute);
            // @ts-expect-error - Safari
            else mql.removeListener(compute);
          };
        }
      }
    } catch (_) { }
  }, []);

  const handleCreateTask = async (input: CreateTaskInput) => {
    try {
      await ApiService.createTask({
        title: input.title,
        description: input.description,
        frequency: input.frequency,
        format: input.itemType,
        startDate: input.startDate,
        endDate: input.endDate,
        ...(input.departmentIds && input.departmentIds.length > 0 ? { departmentIds: input.departmentIds } : {}),
        ...(input.userIds && input.userIds.length > 0 ? { userIds: input.userIds } : {}),
      });

      await showAlert({
        title: "Başarılı",
        message: "Görev başarıyla oluşturuldu",
        type: "success",
      });

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: (error as any)?.message || "Görev oluşturulurken bir hata oluştu",
        type: "error",
      });
    }
  };

  const handleEditTask = async (task: Task) => {
    setSelectedTaskForEdit(task);
    setEditModalOpen(true);
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      const body: any = {};
      if (updates.title !== undefined) body.title = updates.title;
      if (updates.description !== undefined) body.description = updates.description;
      if (updates.frequency !== undefined) body.frequency = String(updates.frequency);
      if (updates.itemType !== undefined) body.format = String(updates.itemType);
      if (updates.startDate !== undefined) body.startDate = updates.startDate;
      if (updates.endDate !== undefined) body.endDate = updates.endDate;
      if (updates.taskType !== undefined) body.taskType = updates.taskType || undefined;
      if ((updates as any).departments) {
        body.departmentIds = ((updates as any).departments as TaskDepartment[]).map((d) => d.departmentId || d.id);
      }
      await ApiService.updateTask(taskId, body);

      await showAlert({
        title: "Başarılı",
        message: "Görev başarıyla güncellendi",
        type: "success",
      });

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: (error as any)?.message || "Görev güncellenirken bir hata oluştu",
        type: "error",
      });
    }
  };

  const handleDeleteTask = async (task: Task) => {
    const confirmed = await showConfirm({
      title: "Görevi Sil",
      message: `"${task.title}" görevini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      try {
        await ApiService.deleteTask(task.id);
        await showAlert({
          title: "Başarılı",
          message: "Görev silindi",
          type: "success",
        });
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        await showAlert({
          title: "Hata",
          message: (error as any)?.message || "Görev silinirken bir hata oluştu",
          type: "error",
        });
      }
    }
  };

  const handleViewSubmissions = (task: Task) => {
    setShowSubmissionsForTask(task.id);
  };

  const handleSubmitTask = (task: UserTask) => {
    setSelectedTask(task);
    setSubmitModalOpen(true);
  };

  const handleTaskSubmission = async (input: SubmitTaskInput) => {
    try {
      await ApiService.submitTask(input.taskId, {
        content: input.content,
      });

      await showAlert({
        title: "Başarılı",
        message: "Göreviniz başarıyla gönderildi",
        type: "success",
      });

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: (error as any)?.message || "Görev gönderilirken bir hata oluştu",
        type: "error",
      });
    }
  };

  const handleViewSubmission = (task: UserTask) => {
    if (task.submission) {
      setSelectedSubmission(task.submission);
      setViewSubmissionModalOpen(true);
    }
  };

  const handleApproveSubmission = async (submission: TaskSubmission) => {
    try {
      await ApiService.reviewTaskSubmission(submission.id, "APPROVE");
      await showAlert({
        title: "Başarılı",
        message: "Gönderim onaylandı",
        type: "success",
      });
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: (error as any)?.message || "Gönderim onaylanırken bir hata oluştu",
        type: "error",
      });
    }
  };

  const handleRejectSubmission = async (submission: TaskSubmission) => {
    try {
      const note = await showPrompt({
        title: "Reddetme Sebebi",
        placeholder: "Örn. İçerik yönergelere uygun değil",
        confirmText: "Reddet",
        cancelText: "Vazgeç",
        multiline: true,
      });

      if (!note) {
        return;
      }

      await ApiService.reviewTaskSubmission(submission.id, "REJECT", note);
      await showAlert({
        title: "Başarılı",
        message: "Gönderim reddedildi",
        type: "success",
      });
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      await showAlert({
        title: "Hata",
        message: (error as any)?.message || "Gönderim reddedilirken bir hata oluştu",
        type: "error",
      });
    }
  };

  const handleViewSubmissionDetail = (submission: TaskSubmission) => {
    setSelectedSubmission(submission);
    setViewSubmissionModalOpen(true);
  };

  const renderTaskItem = useCallback(
    (task: Task, currentViewMode: string = "list") => {
      return (
        <TaskCard
          key={task.id}
          task={task}
          onEdit={canUpdateTask ? handleEditTask : undefined}
          onDelete={canDeleteTask ? handleDeleteTask : undefined}
          onViewSubmissions={canReviewTask ? handleViewSubmissions : undefined}
          viewMode={currentViewMode as "list" | "grid"}
          canUpdate={canUpdateTask}
          canDelete={canDeleteTask}
          canReview={canReviewTask}
        />
      );
    },
    [canUpdateTask, canDeleteTask, canReviewTask]
  );

  const renderSubmissionItem = useCallback(
    (submission: TaskSubmission) => {
      return (
        <TaskSubmissionCard
          key={submission.id}
          submission={submission}
          onApprove={canReviewTask ? handleApproveSubmission : undefined}
          onReject={canReviewTask ? handleRejectSubmission : undefined}
          onView={handleViewSubmissionDetail}
          canReview={canReviewTask}
        />
      );
    },
    [canReviewTask]
  );

  const renderUserTaskItem = useCallback(
    (task: UserTask, currentViewMode: string = "list") => {
      return (
        <UserTaskCard
          key={task.id}
          task={task}
          onSubmit={canSubmitTask ? handleSubmitTask : undefined}
          onViewSubmission={handleViewSubmission}
          viewMode={currentViewMode as "list" | "grid"}
          canSubmit={canSubmitTask}
        />
      );
    },
    [canSubmitTask]
  );

  const renderTaskStatusFilter = useCallback(
    (opts?: { fullWidth?: boolean; onClose?: () => void }) => {
      return (
        <TaskStatusFilter
          selectedStatus={statusFilter}
          onStatusChange={setStatusFilter}
          fullWidth={!!opts?.fullWidth}
          onClose={opts?.onClose}
        />
      );
    },
    [statusFilter]
  );

  const handleCreateFromSuggestion = useCallback((suggestion: TaskSuggestion) => {
    const now = new Date();
    const endDate = new Date();
    endDate.setDate(now.getDate() + 7);

    // topStudents'dan kullanıcı ID'lerini al
    const userIdsFromTopStudents = suggestion.topStudents?.map(student => student.id) || [];
    
    // Eğer departmentIds varsa ve userIds yoksa, sadece departmentIds kullan
    // Eğer userIds varsa (suggestedTask'tan veya topStudents'dan), userIds kullan
    const suggestedUserIds = suggestion.suggestedTask?.userIds || [];
    const finalUserIds = suggestedUserIds.length > 0 ? suggestedUserIds : userIdsFromTopStudents;
    
    // departmentIds varsa userIds kullanma (modal kuralı: ikisi aynı anda seçilemez)
    const hasDepartments = (suggestion.suggestedTask?.departmentIds || []).length > 0;

    const taskInput: CreateTaskInput = {
      title: suggestion.suggestedTask?.title || "",
      description: suggestion.suggestedTask?.description || "",
      itemType: (suggestion.suggestedTask?.itemType as any) || "TEXT",
      frequency: (suggestion.suggestedTask?.frequency as any) || "ONCE",
      startDate: now.toISOString().slice(0, 16),
      endDate: endDate.toISOString().slice(0, 16),
      departmentIds: hasDepartments ? (suggestion.suggestedTask?.departmentIds || []) : [],
      userIds: hasDepartments ? [] : finalUserIds,
    };
    setPrefillTaskData(taskInput);
    setCreateModalOpen(true);
  }, []);

  const adminActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        <button
          onClick={() => {
            setFilterOpen((prev) => !prev);
          }}
          type="button"
          className={`${LIST_TOOLBAR_BTN} ${filterOpen
            ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
            : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
          title="Filtrele"
          aria-pressed={filterOpen}>
          <Filter className={LIST_TOOLBAR_ICON} />
          <span>Filtrele</span>
        </button>
        {canCreateTask && (
          <button
            type="button"
            onClick={() => {
              setPrefillTaskData(null);
              setCreateModalOpen(true);
            }}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Görev</span>
          </button>
        )}
      </div>
    ),
    [filterOpen, canCreateTask]
  );

  const buildTasksPath = useCallback(() => {
    const params: Record<string, string> = {
      sortBy: "createdAt",
      sortOrder: "DESC",
      type: statusFilter,
    };

    const query = new URLSearchParams(params).toString();
    return `tasks?${query}`;
  }, [statusFilter]);

  const adminTab: TabItemProps = useMemo(() => {
    if (showSubmissionsForTask) {
      return {
        type: "custom",
        title: "Tüm Görevler",
        path: "all-tasks",
        tabId: "all-tasks",
        customComponent: (
          <div className="h-full bg-gray-50 p-6 overflow-y-auto">
            <div className="mb-6">
              <button
                onClick={() => setShowSubmissionsForTask(null)}
                className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-2">
                ← Görevlere Geri Dön
              </button>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Görev Gönderimleri</h2>
            <SubmissionsView taskId={showSubmissionsForTask} />
          </div>
        ),
      };
    }

    return {
      type: "list",
      title: "Tüm Görevler",
      path: buildTasksPath(),
      tabId: "all-tasks",
      actions: adminActions,
      isSearch: true,
      showToggle: true,
      leftArea: !isMobileViewport && filterOpen ? () => renderTaskStatusFilter() : undefined,
      gridClassName: "cq-grid-1-2-3-4",
      refreshKey: `tasks-${refreshTrigger}-${statusFilter}`,
      item: renderTaskItem,
      dataProcessor: (data: any[]) => {
        return data.map((t: any) => {
          const departments: TaskDepartment[] = Array.isArray(t.departments)
            ? t.departments.map((d: any) => ({
              id: d.id,
              departmentId: d.id,
              departmentName: d.name,
              color: d.color,
            }))
            : [];
          const itemType = String(t.format || "TEXT")
            .trim()
            .toUpperCase();
          const frequency = String(t.frequency || "ONCE")
            .trim()
            .toUpperCase();

          const statusInfo = t.status || null;
          const localStatus: Task["status"] = statusInfo?.isCompleted
            ? "COMPLETED"
            : statusInfo?.label === "Süresi Doldu"
              ? "EXPIRED"
              : "ACTIVE";

          const assignedUsers = Array.isArray(t.assignedUsers)
            ? t.assignedUsers.map((u: any) => ({
              id: u.id,
              firstName: u.firstName,
              lastName: u.lastName,
            }))
            : [];

          return {
            id: t.id,
            title: t.title,
            description: t.description,
            itemType: itemType as any,
            frequency: frequency as any,
            startDate: t.startDate,
            endDate: t.endDate,
            status: localStatus,
            createdBy: t.createdBy,
            createdAt: t.createdAt,
            creator: t.creator
              ? {
                id: t.creator.id,
                firstName: t.creator.firstName,
                lastName: t.creator.lastName,
                departmentId: t.creator.departmentId,
                media: t.creator.media,
              }
              : undefined,
            departments,
            assignedUsers,
            taskType: t.taskType,
            currentPeriod: t.currentPeriod,
            statusInfo: statusInfo,
            submissionCount: statusInfo?.completedCount,
            totalTargetCount: statusInfo?.totalUsers,
          } as Task;
        });
      },
    };
  }, [
    showSubmissionsForTask,
    refreshTrigger,
    filterOpen,
    statusFilter,
    adminActions,
    renderTaskStatusFilter,
    renderTaskItem,
    buildTasksPath,
  ]);

  const buildUserTasksPath = useCallback(() => {
    const params: Record<string, string> = {
      sortBy: "createdAt",
      sortOrder: "DESC",
    };

    const query = new URLSearchParams(params).toString();
    return `tasks?${query}`;
  }, []);

  const userTab: TabItemProps = useMemo(
    () => ({
      type: "list",
      title: "Atanan Görevler",
      path: buildUserTasksPath(),
      tabId: "assigned-tasks",
      isSearch: true,
      showToggle: true,
      gridClassName: "cq-grid-1-2-3-4",
      refreshKey: `assigned-tasks-${refreshTrigger}`,
      item: renderUserTaskItem,
      emptyMessage: "Size atanmış görev bulunmuyor",
      dataProcessor: (data: any[]) => {
        return data.map((t: any) => {
          const itemType = String(t.format || "TEXT")
            .trim()
            .toUpperCase();
          const frequency = String(t.frequency || "ONCE")
            .trim()
            .toUpperCase();

          const statusInfo = t.status || null;
          const localStatus: UserTask["status"] = statusInfo?.isCompleted
            ? "COMPLETED"
            : statusInfo?.label === "Süresi Doldu"
              ? "EXPIRED"
              : "ACTIVE";

          let hasSubmitted = false;
          let submission: any = undefined;

          if (t.userSubmission) {
            const mySub = t.userSubmission;
            const att = Array.isArray(mySub.attachments) && mySub.attachments.length > 0 ? mySub.attachments[0] : null;
            const statusMap: Record<string, any> = {
              APPROVED: "APPROVED",
              REJECTED: "REJECTED",
              PENDING: "SUBMITTED",
            };
            const submissionStatus = statusMap[String(mySub.status)] || "SUBMITTED";

            hasSubmitted = submissionStatus === "SUBMITTED" || submissionStatus === "APPROVED";

            submission = {
              id: mySub.id,
              taskId: mySub.taskId,
              taskTitle: mySub.task?.title || t.title,
              userId: mySub.submittedBy,
              userName: `${mySub.submitter?.firstName || ""} ${mySub.submitter?.lastName || ""}`.trim() || "Bilinmiyor",
              userDepartment: mySub.submitter?.department?.name || "Bilinmiyor",
              submittedAt: mySub.submittedAt,
              status: submissionStatus,
              content: mySub.content,
              fileUrl: att?.url,
              fileName: att?.name,
              fileSize: att?.size,
              reviewNote: mySub.reviewNote,
            } as TaskSubmission;
          }

          return {
            id: t.id,
            taskId: t.id,
            title: t.title,
            description: t.description,
            itemType: itemType as any,
            frequency: frequency as any,
            startDate: t.startDate,
            endDate: t.endDate,
            status: localStatus,
            hasSubmitted,
            submission,
            creator: t.creator
              ? {
                id: t.creator.id,
                firstName: t.creator.firstName,
                lastName: t.creator.lastName,
                departmentId: t.creator.departmentId,
                media: t.creator.media,
              }
              : undefined,
            statusInfo: statusInfo,
          } as UserTask;
        });
      },
    }),
    [refreshTrigger, renderUserTaskItem, buildUserTasksPath]
  );

  const renderSuggestionItem = useCallback(
    (suggestion: TaskSuggestion, currentViewMode: string = "list") => {
      return (
        <SuggestionCard
          key={`${suggestion.type}-${suggestion.department?.id || 'all'}-${suggestion.lesson?.id || 'all'}`}
          suggestion={suggestion}
          onCreateTask={handleCreateFromSuggestion}
          viewMode={currentViewMode as "list" | "grid"}
        />
      );
    },
    [handleCreateFromSuggestion]
  );

  const fetchSuggestions = useCallback(async () => {
    const response = await ApiService.getApi("tasks/suggestions?limit=20");
    // API { suggestions: [], summary: {} } formatında dönüyor
    // DynamicList { data: [], meta: {} } formatı bekliyor
    return {
      data: response?.suggestions || [],
      meta: {
        page: 1,
        totalPages: 1,
        totalItems: response?.suggestions?.length || 0,
        limit: 20,
      },
    };
  }, []);

  const suggestionsTab: TabItemProps = useMemo(
    () => ({
      type: "list",
      title: "Önerilen Görevler",
      path: "tasks/suggestions",
      tabId: "suggested-tasks",
      isSearch: false,
      showToggle: true,
      isPagination: false,
      gridClassName: "cq-grid-1-2-3-4",
      emptyMessage: "Harika! Tüm kişilerin ödevleri güncel, görev önerisi bulunmuyor.",
      refreshKey: `suggestions-${refreshTrigger}`,
      item: renderSuggestionItem,
      fetcher: fetchSuggestions,
    }),
    [renderSuggestionItem, refreshTrigger, fetchSuggestions]
  );

  const tabs = useMemo(() => {
    const tabList: TabItemProps[] = [];
    if (canReviewTask) {
      tabList.push(adminTab);
    }
    if (canListTasks) {
      tabList.push(userTab);
    }
    if (canCreateTask) {
      tabList.push(suggestionsTab);
    }
    return tabList;
  }, [adminTab, userTab, suggestionsTab, canReviewTask, canListTasks, canCreateTask]);

  function SubmissionsView({ taskId }: { taskId: string }) {
    const [submissions, setSubmissions] = useState<TaskSubmission[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchSubmissions = async () => {
        setLoading(true);
        try {
          const res = await ApiService.getTaskSubmissions(taskId);
          const list = Array.isArray(res) ? res : (res as any)?.data || [];
          const mapped: TaskSubmission[] = list.map((s: any) => {
            const att = Array.isArray(s.attachments) && s.attachments.length > 0 ? s.attachments[0] : null;
            const statusMap: Record<string, TaskSubmission["status"]> = {
              APPROVED: "APPROVED",
              REJECTED: "REJECTED",
              PENDING: "SUBMITTED",
            };
            return {
              id: s.id,
              taskId: s.taskId,
              taskTitle: s.task?.title || "",
              userId: s.submittedBy,
              userName: `${s.submitter?.firstName || ""} ${s.submitter?.lastName || ""}`.trim() || "Bilinmiyor",
              userDepartment: s.submitter?.department?.name || "Bilinmiyor",
              submittedAt: s.submittedAt,
              status: statusMap[String(s.status)] || "SUBMITTED",
              content: s.content,
              fileUrl: att?.url,
              fileName: att?.name,
              fileSize: att?.size,
              reviewNote: s.reviewNote,
            } as TaskSubmission;
          });
          setSubmissions(mapped);
        } finally {
          setLoading(false);
        }
      };
      fetchSubmissions();
    }, [taskId, refreshTrigger]);

    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      );
    }

    if (submissions.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">Henüz gönderim bulunmuyor</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {submissions.map((submission) => renderSubmissionItem(submission))}
      </div>
    );
  }

  return (
    <>

      <TabPage title="Görevler" tabs={tabs} />

      {/* Mobile filter overlay (Employees style) */}
      {isMobileViewport && filterOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setFilterOpen(false)}
            aria-label="Filtre panelini kapat"
          />
          <div className="fixed left-3 right-3 top-[188px] z-50 max-h-[65vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
            {renderTaskStatusFilter({ fullWidth: true, onClose: () => setFilterOpen(false) })}
          </div>
        </>
      )}

      <CreateTaskModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setPrefillTaskData(null);
        }}
        onSubmit={handleCreateTask}
        initialData={prefillTaskData || undefined}
      />

      <EditTaskModal
        isOpen={editModalOpen}
        task={selectedTaskForEdit}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedTaskForEdit(null);
        }}
        onSubmit={handleUpdateTask}
      />

      <SubmitTaskModal
        isOpen={submitModalOpen}
        task={selectedTask}
        onClose={() => {
          setSubmitModalOpen(false);
          setSelectedTask(null);
        }}
        onSubmit={handleTaskSubmission}
      />

      <ViewSubmissionModal
        isOpen={viewSubmissionModalOpen}
        submission={selectedSubmission}
        onClose={() => {
          setViewSubmissionModalOpen(false);
          setSelectedSubmission(null);
        }}
      />
    </>
  );
}